import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Image, QrCode } from 'lucide-react';
import { useEffect, useRef, useState, type RefObject } from 'react';

import { useCameraDoLeitor } from '@/lib/camera-do-leitor';
import { lerQrDeImagem } from '@/lib/leitor-de-qr';
import {
  chegouEmRajada,
  enterDoLeitor,
  resolverAdesivo,
  retornoDaLeitura,
  retornoDoRecado,
  type AlvoDoAdesivo,
  type LeituraDoAdesivo,
} from '@/lib/leitura-do-adesivo';
import { anunciar } from '@/lib/retorno';
import { pedir } from '@/modulos/equipamentos/api';
import { CHAVE_FROTA } from '@/modulos/equipamentos/consultas-frota';
import { lerEquipamento, type EquipamentoDaFrota } from '@/modulos/equipamentos/frota';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { Botao, BotaoFechar } from './Botao';
import type { OpcaoDeSelecao } from './Campo';
import { Janela } from './JanelaDeAcao';

/**
 * O LEITOR DO ADESIVO NO CAMPO DA MÁQUINA, na mesa (pedido do dono,
 * 31/10/2026: "um leitor que leia o QR e identifique o equipamento
 * automaticamente, assim zeramos a chance de erros de preenchimento").
 *
 * Mora AO LADO do campo (`ListaDeEscolha` com `adesivo`), e não em cada tela:
 * todo campo de máquina ganha o mesmo botão, a mesma janela e o mesmo leitor
 * que digita, por uma propriedade só. Três caminhos, uma regra
 * (`lib/leitura-do-adesivo`):
 *
 * 1. **A câmera** — o notebook e o tablet da oficina têm webcam. O botão
 *    `qr-code` abre a janela com o vídeo, que lê sozinho e fecha ao preencher.
 * 2. **A foto** — o computador sem câmera, ou com ela bloqueada: "Ler de uma
 *    foto" lê o QR da imagem escolhida.
 * 3. **O leitor que digita** (USB ou Bluetooth, modo teclado): ele escreve o
 *    conteúdo do QR no campo em rajada e aperta Enter. Só o Enter que é de
 *    leitura (`enterDoLeitor`) é tomado da lista; quem digita "BAS" + Enter
 *    na busca continua escolhendo como sempre.
 *
 * ## Dentro de outra janela, o leitor abre no lugar
 *
 * "Nunca dois modais empilhados" (p.30). O campo que já mora numa janela (o
 * estudo de renovação, a inspeção preditiva) abre o leitor logo abaixo dele,
 * dentro da mesma janela, em vez de abrir outra por cima.
 *
 * ## O que ele diz
 *
 * O que preencheu vira toast com o código e as etiquetas (código interno e
 * placa) que o campo mostra: a pessoa confere sem abrir a lista. O que não
 * preencheu diz por quê e o que fazer, e o campo fica como estava: a máquina
 * que existe mas não está NESTA lista é dita assim, e não como "não existe".
 */
export function LeitorDoAdesivoDoCampo({
  caixa,
  rotulo,
  opcoes,
  aoEscolher,
  aoRecusar,
  porQueFora,
}: {
  /** O campo inteiro (gatilho e lista): é nele que o leitor que digita escreve. */
  readonly caixa: RefObject<HTMLDivElement | null>;
  readonly rotulo: string;
  readonly opcoes: readonly OpcaoDeSelecao[];
  /** Escolhe a opção pelo `valor` e fecha a lista. */
  readonly aoEscolher: (valor: string) => void;
  /** A leitura não preencheu: fecha a lista, se aberta, e devolve o foco ao campo. */
  readonly aoRecusar: () => void;
  readonly porQueFora?: string | undefined;
}) {
  const lugarDoBotao = useRef<HTMLSpanElement>(null);
  const [aberto, setAberto] = useState<'janela' | 'no-lugar' | null>(null);
  const universo = useUniverso(aberto !== null);
  const cliente = useQueryClient();

  /* Tudo o que o leitor que digita usa, numa referência: o ouvinte é do DOM e
     vive enquanto o campo vive, e as opções mudam sem ele ser trocado. */
  const atual = useRef({ opcoes, aoEscolher, aoRecusar, porQueFora, universo });
  atual.current = { opcoes, aoEscolher, aoRecusar, porQueFora, universo };

  /* O LEITOR QUE DIGITA. Escuta na captura, no campo inteiro (o gatilho, ou a
     busca da lista aberta), para ver o Enter ANTES da lista: o Enter que é de
     leitura não chega a ela. Cada tecla de texto entra na rajada com a hora;
     o Esc, o Tab e a pausa longa recomeçam. */
  useEffect(() => {
    const raiz = caixa.current;
    if (!raiz) return undefined;
    let teclas: { tecla: string; quando: number }[] = [];
    const ouvir = (evento: KeyboardEvent) => {
      if (evento.target instanceof Element && evento.target.closest('[data-leitor-do-adesivo]')) return;
      const agora = evento.timeStamp || performance.now();
      const ultima = teclas.at(-1);
      if (ultima !== undefined && agora - ultima.quando > PAUSA_QUE_RECOMECA_MS) teclas = [];
      if (evento.key === 'Enter') {
        const texto = teclas.map((t) => t.tecla).join('');
        const rapida = chegouEmRajada(teclas.map((t) => t.quando));
        teclas = [];
        const { opcoes: daLista } = atual.current;
        if (!enterDoLeitor(texto, rapida, daLista)) return;
        evento.preventDefault();
        evento.stopPropagation();
        void resolverComAFrota(texto).then((lida) => {
          entregar(lida);
        });
        return;
      }
      if (evento.key === 'Backspace') {
        teclas.pop();
        return;
      }
      if (evento.key.length === 1 && !evento.ctrlKey && !evento.metaKey && !evento.altKey) {
        teclas.push({ tecla: evento.key, quando: agora });
        return;
      }
      if (evento.key === 'Escape' || evento.key === 'Tab') teclas = [];
    };

    /* Do teclado a resposta pode esperar a frota: quem lê a frota fica
       sabendo se o código existe fora desta lista. */
    const resolverComAFrota = async (texto: string): Promise<LeituraDoAdesivo<OpcaoDeSelecao>> => {
      const { opcoes: daLista, porQueFora: porQue, universo: frota } = atual.current;
      const primeira = resolverAdesivo(texto, { opcoes: daLista, universo: frota.dados, porQueFora: porQue });
      if (primeira.tipo !== 'recado' || frota.dados !== undefined || !frota.pode) return primeira;
      const buscada = await cliente.fetchQuery({ queryKey: CHAVE_FROTA, queryFn: pedirFrota, staleTime: 60_000 }).catch(() => undefined);
      return resolverAdesivo(texto, { opcoes: daLista, universo: buscada, porQueFora: porQue });
    };

    const entregar = (lida: LeituraDoAdesivo<OpcaoDeSelecao>) => {
      if (lida.tipo === 'escolhida') {
        atual.current.aoEscolher(lida.opcao.valor);
        anunciar(retornoDaLeitura(lida.opcao));
      } else if (lida.tipo === 'recado') {
        atual.current.aoRecusar();
        anunciar(retornoDoRecado(lida.recado, lida.codigo));
      }
    };

    raiz.addEventListener('keydown', ouvir, true);
    return () => {
      raiz.removeEventListener('keydown', ouvir, true);
    };
  }, [caixa, cliente]);

  const fechar = () => {
    setAberto(null);
    lugarDoBotao.current?.querySelector('button')?.focus();
  };
  const preencher = (opcao: OpcaoDeSelecao) => {
    setAberto(null);
    aoEscolher(opcao.valor);
    anunciar(retornoDaLeitura(opcao));
  };

  const painel = (
    <PainelDoLeitor
      opcoes={opcoes}
      universo={universo.dados}
      porQueFora={porQueFora}
      aoPreencher={preencher}
      aoFechar={fechar}
    />
  );

  return (
    <>
      <span ref={lugarDoBotao} data-leitor-do-adesivo className="flex">
        <Botao
          icone
          nivel="secundario"
          aria-label={`Ler QR do adesivo para ${rotulo}`}
          title="Ler QR do adesivo"
          aria-expanded={aberto !== null}
          onClick={() => {
            if (aberto !== null) {
              setAberto(null);
              return;
            }
            /* Dentro de outra janela, no lugar: nunca dois modais (p.30). */
            setAberto(lugarDoBotao.current?.closest('dialog') ? 'no-lugar' : 'janela');
          }}
        >
          <QrCode aria-hidden="true" className="size-[var(--icone-controle)]" />
        </Botao>
      </span>
      {aberto === 'no-lugar' ? (
        <div data-leitor-do-adesivo className="col-span-2 mt-2 flex flex-col gap-3 rounded-card border border-borda-sutil bg-rebaixada p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="txt-apoio text-tinta-secundaria">Leitura do adesivo</span>
            <BotaoFechar rotulo="Fechar o leitor" aoFechar={fechar} />
          </div>
          {painel}
        </div>
      ) : null}
      {aberto === 'janela' ? (
        <span data-leitor-do-adesivo className="contents">
          <Janela
            titulo="Ler o adesivo da máquina"
            descricao="Aponte a câmera para o QR colado na máquina. O campo se preenche sozinho."
            largura="p"
            aberta
            aoFechar={fechar}
          >
            {painel}
          </Janela>
        </span>
      ) : null}
    </>
  );
}

/** Uma pausa maior que esta entre duas teclas recomeça a leitura do teclado. */
const PAUSA_QUE_RECOMECA_MS = 1_500;

async function pedirFrota(): Promise<readonly EquipamentoDaFrota[]> {
  const corpo = await pedir<{ equipamentos: Record<string, unknown>[] }>('/equipamentos');
  return corpo.equipamentos.map(lerEquipamento);
}

/**
 * A frota, para dizer "existe, mas não está nesta lista". Só quem a lê
 * (`equipamento.ler`, fora do posto parceiro, que o servidor recusa) e só com
 * o leitor aberto: o campo fechado não pede nada a mais.
 */
function useUniverso(ligado: boolean): { readonly pode: boolean; readonly dados: readonly AlvoDoAdesivo[] | undefined } {
  const sessao = useSessaoEmMemoria();
  const pode = (sessao?.permissoes.includes('equipamento.ler') ?? false) && sessao?.parceiro === undefined;
  const frota = useQuery({
    queryKey: CHAVE_FROTA,
    enabled: ligado && pode,
    queryFn: pedirFrota,
    meta: { silenciarFalha: true, oQueLia: 'a frota' },
  });
  return { pode, dados: frota.data };
}

/**
 * A câmera, a mira e a foto. Lê sozinho, sem botão de disparo: ou o quadro
 * tem o código ou não tem, e pedir um clique depois de enquadrar seria
 * inventar um passo.
 */
function PainelDoLeitor({
  opcoes,
  universo,
  porQueFora,
  aoPreencher,
  aoFechar,
}: {
  readonly opcoes: readonly OpcaoDeSelecao[];
  readonly universo: readonly AlvoDoAdesivo[] | undefined;
  readonly porQueFora: string | undefined;
  readonly aoPreencher: (opcao: OpcaoDeSelecao) => void;
  readonly aoFechar: () => void;
}) {
  const arquivo = useRef<HTMLInputElement>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const contexto = { opcoes, universo, porQueFora };

  const ler = (texto: string): boolean => {
    const lida = resolverAdesivo(texto, contexto);
    if (lida.tipo === 'escolhida') {
      aoPreencher(lida.opcao);
      return true;
    }
    if (lida.tipo === 'recado') setRecado(lida.recado);
    return false;
  };
  const camera = useCameraDoLeitor(ler);

  return (
    <div className="flex flex-col gap-3">
      {camera.fechada === null ? (
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-card bg-preto-obra">
          <video
            ref={camera.video}
            playsInline
            muted
            autoPlay
            aria-label="A câmera, para ler o adesivo da máquina"
            className="size-full object-cover"
          />
          {/* A mira é QUADRADA, como o QR: mira com outra forma ensina a enquadrar errado. */}
          <span aria-hidden="true" className="pointer-events-none absolute inset-y-8 left-1/2 aspect-square -translate-x-1/2">
            <span className="absolute top-0 left-0 size-8 rounded-tl-[var(--raio-3)] border-t-4 border-l-4 border-branco" />
            <span className="absolute top-0 right-0 size-8 rounded-tr-[var(--raio-3)] border-t-4 border-r-4 border-branco" />
            <span className="absolute bottom-0 left-0 size-8 rounded-bl-[var(--raio-3)] border-b-4 border-l-4 border-branco" />
            <span className="absolute right-0 bottom-0 size-8 rounded-br-[var(--raio-3)] border-r-4 border-b-4 border-branco" />
          </span>
        </div>
      ) : (
        <div role="status" className="flex flex-col items-center gap-3 rounded-card border border-borda-sutil bg-card px-4 py-6 text-center">
          <span aria-hidden="true" className="flex size-12 items-center justify-center rounded-controle bg-rebaixada">
            <QrCode className="size-[var(--icone-destaque)] text-tinta-secundaria" />
          </span>
          <p className="txt-corpo text-tinta">{camera.fechada}</p>
          {recado === null ? null : <p className="txt-apoio text-tinta-secundaria">{recado}</p>}
        </div>
      )}
      {camera.fechada === null ? (
        /* O recado vai ABAIXO do vídeo, e não sobre ele: a frase de "existe,
           mas não está nesta lista" tem três linhas e cobriria a mira. */
        <p role="status" className={`txt-apoio ${recado === null ? 'text-tinta-secundaria' : 'text-tinta'}`}>
          {recado ?? (camera.abrindo ? 'Abrindo a câmera. Se o navegador perguntar, permita a câmera.' : 'Aponte para o adesivo da máquina. A leitura é sozinha.')}
        </p>
      ) : null}
      <input
        ref={arquivo}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        aria-label="Foto do adesivo"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          void lerQrDeImagem(f).then((texto) => {
            if (texto === null) {
              setRecado('Não achei o QR na foto. Fotografe o adesivo mais de perto, de frente e com luz, e tente de novo.');
              return;
            }
            ler(texto);
          });
        }}
      />
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Botao nivel="fantasma" onClick={aoFechar}>
          Escolher pela lista
        </Botao>
        <Botao nivel="secundario" onClick={() => arquivo.current?.click()}>
          <Image aria-hidden="true" className="size-[var(--icone-controle)]" />
          Ler de uma foto
        </Botao>
      </div>
    </div>
  );
}
